import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";

import ListPagination from "../../containers/ListPagination";
import { useRuleStyles } from "../Rules/styles";
import { sortDirection } from "../Rules/common";

const contact = (team, key) => team.contactInformation?.[key] ?? "";

/** `creationDate` arrives as "2021-09-28" or [2021, 9, 28] depending on the serializer. */
export const formatDate = (value) => {
  if (Array.isArray(value)) {
    const [y, m, d] = value;
    return [y, String(m).padStart(2, "0"), String(d).padStart(2, "0")].join(
      "-",
    );
  }
  return value ? String(value).slice(0, 10) : "";
};

/** `sort` is the backend sort token (admin/teams `sort`); columns without one don't sort. */
export const COLUMNS = [
  { key: "id", label: "ID", sort: "Id", value: (t) => t.id, numeric: true },
  {
    key: "name",
    label: "Name",
    sort: "Name",
    value: (t) => t.name,
    opensEditor: true,
  },
  {
    key: "abbreviation",
    label: "Abbreviation",
    sort: "Abbreviation",
    value: (t) => t.abbreviation,
  },
  {
    key: "teamType",
    label: "Type",
    sort: "TeamType",
    value: (t) => t.teamType,
  },
  {
    key: "divisionId",
    label: "Division",
    sort: "DivisionId",
    value: (t) => t.divisionId,
  },
  {
    key: "conference",
    label: "Conference",
    sort: "Conference",
    value: (t) => t.conference?.name,
  },
  {
    key: "mascotName",
    label: "Plural mascot name",
    sort: "MascotName",
    value: (t) => t.mascotName,
  },
  {
    key: "regionId",
    label: "Region",
    sort: "RegionId",
    value: (t) => t.regionId,
  },
  {
    key: "city",
    label: "City",
    sort: "City",
    value: (t) => contact(t, "city"),
  },
  {
    key: "state",
    label: "State",
    sort: "State",
    value: (t) => contact(t, "state"),
  },
  {
    key: "country",
    label: "Country",
    sort: "Country",
    value: (t) => contact(t, "country"),
  },
  {
    key: "governingBodies",
    label: "Governing bodies",
    value: (t) =>
      (t.governingBodies || [])
        .map((gb) => gb?.abbreviation)
        .filter(Boolean)
        .join(", "),
  },
  {
    key: "userCount",
    label: "Users",
    value: (t) => t.userCount,
    numeric: true,
  },
  {
    key: "updatedDate",
    label: "Updated",
    sort: "UpdatedDate",
    // Older backends send only creationDate.
    value: (t) => formatDate(t.updatedDate || t.creationDate),
  },
];

const TeamsTable = ({
  rows,
  state,
  total,
  loading,
  onSort,
  onPage,
  onEdit,
  onDelete,
}) => {
  const classes = useRuleStyles();
  const columnCount = COLUMNS.length + 1;

  return (
    <TableContainer component={Paper}>
      {/* Rows stay on screen while the next page loads. */}
      <Box height={4}>
        {loading && <LinearProgress aria-label="Loading teams" />}
      </Box>
      <Table size="small" aria-label="Teams" aria-busy={loading}>
        <TableHead>
          <TableRow>
            {COLUMNS.map((col) => (
              <TableCell
                key={col.key}
                scope="col"
                className={`${classes.headerCell}${
                  col.numeric ? ` ${classes.number}` : ""
                }`}
                sortDirection={
                  col.sort ? sortDirection(state, col.sort) : false
                }
              >
                {col.sort ? (
                  <TableSortLabel
                    active={state.sort === col.sort}
                    direction={sortDirection(state, col.sort) || "asc"}
                    onClick={() => onSort(col.sort)}
                  >
                    {col.label}
                  </TableSortLabel>
                ) : (
                  col.label
                )}
              </TableCell>
            ))}
            <TableCell scope="col" className={classes.headerCell}>
              <span className="sr-only">Actions</span>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columnCount} className={classes.empty}>
                No teams match these filters.
              </TableCell>
            </TableRow>
          )}
          {rows.map((team) => (
            <TableRow key={team.id} hover>
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.key}
                  className={`${classes.cell}${
                    col.numeric ? ` ${classes.number}` : ""
                  }`}
                >
                  {col.opensEditor ? (
                    <Link
                      component="button"
                      className={classes.nameLink}
                      onClick={() => onEdit(team)}
                    >
                      {col.value(team) || `Team ${team.id}`}
                    </Link>
                  ) : (
                    col.value(team) ?? ""
                  )}
                </TableCell>
              ))}
              <TableCell className={classes.cell}>
                <Box className={classes.actions}>
                  <IconButton
                    className={classes.iconButton}
                    aria-label={`Edit ${team.name || `team ${team.id}`}`}
                    title="Edit"
                    onClick={() => onEdit(team)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    className={classes.iconButton}
                    aria-label={`Delete ${team.name || `team ${team.id}`}`}
                    title="Delete"
                    onClick={() => onDelete(team)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <ListPagination
        filters={{ page: state.page, limit: state.size, total }}
        fetchFunction={onPage}
      />
    </TableContainer>
  );
};

TeamsTable.propTypes = {
  rows: PropTypes.array.isRequired,
  state: PropTypes.object.isRequired,
  total: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  onSort: PropTypes.func.isRequired,
  onPage: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default TeamsTable;
